import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Switch } from "@comps/ui/switch";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { BarChartWidgetType, Dashboard, KPIWidgetType, LineChartWidgetType, PieChartWidgetType, RadarChartWidgetType, TextWidgetType, Widget } from "@dto/Dashboard";
import { DashboardRightType, FullRightAccess, hasGlobalRightAccess, ReadRightAccess } from "@dto/Users";
import { addElement } from "@utils/arrays";
import { BASE_URL } from "@utils/constants";
import { toastError } from "@utils/toasts";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_DashboardWidgetMenuItem, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Copy, Edit2, EllipsisVertical, Plus, Trash2 } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { BarChartCard } from "./BarChartCard";
import { KPICard } from "./KPICard";
import { LineChartCard } from "./LineChartCard";
import { PieChartCard } from "./PieChartCard";
import { RadarChartCard } from "./RadarChartCard";
import { TextCard } from "./TextCard";
import { WidgetConfig } from "./WidgetConfig";
import { GRAY_300_COLOR } from "@utils/colors";
interface DashboardPageProps {
    dashboard: Dashboard;
}

const DraggingWidgetContext = createContext<{ draggingWidget: { lineIndex: number, widgetIndex: number } | null }>({ draggingWidget: null });

export function DashboardPage({ dashboard }: DashboardPageProps) {
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const [editMode, setEditMode] = useState(false);
    const [draggingWidget, setDraggingWidget] = useState<{ draggingWidget: { lineIndex: number, widgetIndex: number } | null }>({ draggingWidget: null });
    const { t } = useTranslation();
    const user = useUserStore(state => state.user);
    const userRole = useModelStore(state => state.userRole);

    useEffect(() => {
        setEditMode(false);
    }, [dashboard._key]);

    const style = {
        scrollbarColor: wsColor + " " + wsLightColor
    }

    const hasReadAccess = !dashboard.ownerKey || dashboard.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, DashboardRightType, ReadRightAccess, dashboard._key, true);
    if (!hasReadAccess) {
        return (
            <div className="flex flex-col justify-center items-center h-full w-full">
                {translateHtml(t, "dashboard.no.access.text", [], "text-red-500 text-xl font-bold text-center")}
            </div>
        )
    }

    const hasFullDashboardAccess = dashboard.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, DashboardRightType, FullRightAccess, dashboard._key, true);
    return (
        <DraggingWidgetContext.Provider value={draggingWidget}>
            <div className="flex flex-col justify-start items-evenly gap-2 w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-compozz-purple scrollbar-track-compozz-purple-light scrollbar-button-hidden p-2" style={style}>
                {hasFullDashboardAccess && <div className="flex flex-row justify-end items-center w-full gap-2">
                    <Switch checked={editMode} onCheckedChange={setEditMode} style={{ backgroundColor: editMode ? wsColor : GRAY_300_COLOR }} />
                    <Label className="text-black text-lg font-normal">{t("dashboard.button.edit.mode.text")}</Label>
                </div>}
                {dashboard.lines && dashboard.lines.map((line, lineIndex) => {
                    return (
                        <DashboardLine key={dashboard._key + "-" + lineIndex} dashboard={dashboard} lineIndex={lineIndex} editMode={editMode}>
                            {line.widgets && line.widgets.map((widget, widgetIndex) => {
                                return <WidgetContainer key={dashboard._key + "-" + lineIndex + "-" + widgetIndex} dashboard={dashboard} lineIndex={lineIndex} widgetIndex={widgetIndex} widget={widget} editMode={editMode} lineNumber={dashboard.lines.length} widgetNumber={line.widgets.length} setDraggingWidget={setDraggingWidget} />
                            })}
                        </DashboardLine>
                    )
                })}
                {editMode && !dashboard.lines && <div className="flex flex-col justify-center items-center h-full w-full">
                    <NewFirstWidgetButton dashboard={dashboard} />
                </div>}
                {!editMode && !dashboard.lines && <div className="flex flex-col justify-center items-center h-full w-full">
                    {translateHtml(t, "dashboard.no.widgets.text", [], "text-gray-800 text-xl font-bold text-center")}
                </div>}

            </div>
        </DraggingWidgetContext.Provider>
    );
}


interface DashboardLineProps {
    dashboard: Dashboard;
    lineIndex: number;
    editMode: boolean;
    children: React.ReactNode;
}

function DashboardLine({ dashboard, lineIndex, editMode, children }: DashboardLineProps) {
    const workspace = useModelStore(state => state.selectedWorkspace);
    const dashboards = useModelStore(state => state.dashboards);
    const updateDashboards = useModelStore(state => state.updateDashboards);
    const updateSelectedDashboard = useModelStore(state => state.updateSelectedDashboard);
    const [isDraggingOverBottom, setIsDraggingOverBottom] = useState(false);
    const [isDraggingOverTop, setIsDraggingOverTop] = useState(false);
    const { draggingWidget } = useContext(DraggingWidgetContext);
    const [isHover, setIsHover] = useState(false);
    const wsColor = useModelStore(state => state.wsColor);
    const { t } = useTranslation();

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const height = rect.bottom - rect.top;
        const y = e.clientY - rect.top;
        if (y > height * 3 / 4) {
            setIsDraggingOverBottom(true);
            setIsDraggingOverTop(false);
        } else if (y < height / 4) {
            setIsDraggingOverBottom(false);
            setIsDraggingOverTop(true);
        } else {
            setIsDraggingOverBottom(false);
            setIsDraggingOverTop(false);
        }
    }

    const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDraggingOverBottom(false);
            setIsDraggingOverTop(false);
        }
    }

    function dropWidget() {

        if (!draggingWidget || (!isDraggingOverBottom && !isDraggingOverTop)) {
            return;
        }

        const dragLineIndex = draggingWidget.lineIndex;
        const dragWidgetIndex = draggingWidget.widgetIndex;

        let newLines = dashboard.lines ?? [];
        if (lineIndex >= newLines.length) {
            return;
        }

        let newWidgetsOnDragLine = newLines[dragLineIndex].widgets ?? [];
        if (dragWidgetIndex >= newWidgetsOnDragLine.length) {
            return;
        }

        const movedWidget = newWidgetsOnDragLine.splice(dragWidgetIndex, 1);

        if (!movedWidget) {
            return;
        }

        if (newWidgetsOnDragLine.length === 0) {
            newLines.splice(dragLineIndex, 1);
        } else {
            newLines[dragLineIndex].widgets = newWidgetsOnDragLine;
        }

        let newLineIndex = lineIndex;

        if (isDraggingOverBottom) {
            newLineIndex++;
        }

        newLines = addElement(newLines, { widgets: [movedWidget[0]] }, newLineIndex);

        const newDashboard: Dashboard = {
            ...dashboard,
            lines: newLines
        }
        put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    console.error("Error adding widget to dashboard", response);
                }
            } else {
                const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                updateDashboards(newDashboards);
                updateSelectedDashboard(newDashboard);
                // setIsDialogOpen(false);
            }
        }).finally(() => {
            setIsDraggingOverBottom(false);
            setIsDraggingOverTop(false);
        })

    }

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dropWidget();
    }

    const lineStyle = {
        borderBottom: isDraggingOverBottom ? "10px solid " + wsColor : "none",
        borderTop: isDraggingOverTop ? "10px solid " + wsColor : "none",
        transition: "all 0.2s ease-in-out",
        transform: isDraggingOverBottom ? "translateY(-20px)" : isDraggingOverTop ? "translateY(20px)" : "translateY(0px)"
    }

    const zIndex = (isHover && !isDraggingOverBottom && !isDraggingOverTop) ? "z-10" : "z-0";

    return (
        <div key={dashboard._key + "-" + lineIndex} className={`flex flex-row gap-2 justify-center items-center ${zIndex}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)} style={lineStyle}>
            {children}
        </div>
    );
}


interface WidgetContainerProps {
    dashboard: Dashboard;
    lineIndex: number;
    widgetIndex: number;
    widget: Widget;
    editMode: boolean;
    lineNumber: number;
    widgetNumber: number;
    setDraggingWidget: (draggingWidget: { draggingWidget: { lineIndex: number, widgetIndex: number } | null }) => void;
}

function WidgetContainer({ dashboard, lineIndex, widgetIndex, widget, editMode, lineNumber, widgetNumber, setDraggingWidget }: WidgetContainerProps) {
    const [isHover, setIsHover] = useState(false);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const dashboards = useModelStore(state => state.dashboards);
    const updateDashboards = useModelStore(state => state.updateDashboards);
    const updateSelectedDashboard = useModelStore(state => state.updateSelectedDashboard);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingOverLeft, setIsDraggingOverLeft] = useState(false);
    const [isDraggingOverRight, setIsDraggingOverRight] = useState(false);
    const wsColor = useModelStore(state => state.wsColor);
    const { draggingWidget } = useContext(DraggingWidgetContext);
    const { t } = useTranslation();

    const onEditConfirmed = (lineIndex: number, widgetIndex: number, newWidget: Widget) => {

        let newLines = dashboard.lines ?? [];
        if (lineIndex >= newLines.length) {
            return;
        }

        let newWidgets = newLines[lineIndex].widgets ?? [];
        if (widgetIndex >= newWidgets.length) {
            return;
        }

        newWidgets = newWidgets.map((w, i) => i === widgetIndex ? newWidget : w);
        newLines[lineIndex].widgets = newWidgets;

        const newDashboard: Dashboard = {
            ...dashboard,
            lines: newLines
        }
        put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    console.error("Error updating widget to dashboard", response);
                }
            } else {
                const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                updateDashboards(newDashboards);
                updateSelectedDashboard(newDashboard);
            }
            setIsHover(false);
        })
    }

    const onDuplicateConfirmed = (lineIndex: number, widgetIndex: number) => {
        let newLines = dashboard.lines ?? [];
        if (lineIndex >= newLines.length) {
            return;
        }

        let newWidgets = newLines[lineIndex].widgets ?? [];
        if (widgetIndex >= newWidgets.length) {
            return;
        }

        const widgetCopy = { ...newWidgets[widgetIndex] };
        newWidgets = addElement(newWidgets, widgetCopy, widgetIndex + 1);
        newLines[lineIndex].widgets = newWidgets;

        const newDashboard: Dashboard = {
            ...dashboard,
            lines: newLines
        }
        put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    console.error("Error duplicating widget in dashboard", response);
                }
            } else {
                const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                updateDashboards(newDashboards);
                updateSelectedDashboard(newDashboard);
            }
            setIsHover(false);
        })
    }

    const onDeleteConfirmed = (lineIndex: number, widgetIndex: number) => {
        let newLines = dashboard.lines ?? [];
        if (lineIndex >= newLines.length) {
            return;
        }

        let newWidgets = newLines[lineIndex].widgets ?? [];
        if (widgetIndex >= newWidgets.length) {
            return;
        }

        newWidgets.splice(widgetIndex, 1);

        if (newWidgets.length === 0) {
            newLines.splice(lineIndex, 1);
        } else {
            newLines[lineIndex].widgets = newWidgets;
        }

        const newDashboard: Dashboard = {
            ...dashboard,
            lines: newLines
        }
        put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    console.error("Error deleting widget to dashboard", response);
                }
            } else {
                const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                updateDashboards(newDashboards);
                updateSelectedDashboard(newDashboard);
            }
            setIsHover(false);
        })

    }

    function addWidget(widget: Widget, pos: "top" | "right" | "bottom" | "left") {
        let newLineIndex = lineIndex;
        let newWidgetIndex = widgetIndex;
        if (pos === "bottom") {
            newLineIndex = lineIndex + 1;
            newWidgetIndex = 0;
        }
        if (pos === "right") {
            newWidgetIndex = widgetIndex + 1;
        }
        if (widget) {
            let newLines = dashboard.lines ?? [];
            if (pos === "top" || pos === "bottom") {
                newLines = addElement(newLines, { widgets: [widget] }, newLineIndex);
            } else {
                if (newLineIndex >= newLines.length) {
                    newLines.push({
                        widgets: [widget],
                    });
                } else {
                    let newWidgets = newLines[newLineIndex].widgets;
                    newWidgets = addElement(newWidgets, widget, newWidgetIndex);
                    newLines[newLineIndex].widgets = newWidgets;
                }
            }
            const newDashboard: Dashboard = {
                ...dashboard,
                lines: newLines
            }
            put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        console.error("Error adding widget to dashboard", response);
                    }
                } else {
                    const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                    updateDashboards(newDashboards);
                    updateSelectedDashboard(newDashboard);
                    // setIsDialogOpen(false);
                }
                setIsHover(false);
            })
        }
    }

    function dropWidget() {

        if (!draggingWidget || (!isDraggingOverLeft && !isDraggingOverRight)) {
            return;
        }

        const dragLineIndex = draggingWidget.lineIndex;
        const dragWidgetIndex = draggingWidget.widgetIndex;

        let newLines = dashboard.lines ?? [];
        if (lineIndex >= newLines.length) {
            return;
        }

        let newWidgetsOnDragLine = newLines[dragLineIndex].widgets ?? [];
        if (dragWidgetIndex >= newWidgetsOnDragLine.length) {
            return;
        }

        const movedWidget = newWidgetsOnDragLine.splice(dragWidgetIndex, 1);

        if (!movedWidget) {
            return;
        }

        let newLineIndex = lineIndex;
        if (newWidgetsOnDragLine.length === 0) {
            newLines.splice(dragLineIndex, 1);
            newLineIndex--;
        } else {
            newLines[dragLineIndex].widgets = newWidgetsOnDragLine;
        }

        let newWidgetIndex = widgetIndex;
        if (dragLineIndex === lineIndex && dragWidgetIndex < widgetIndex) {
            newWidgetIndex--;
        }

        if (isDraggingOverRight) {
            newWidgetIndex++;
        }

        let newWidgetsOnDropLine = newLines[newLineIndex].widgets ?? [];
        newWidgetsOnDropLine = addElement(newWidgetsOnDropLine, movedWidget[0], newWidgetIndex);
        newLines[newLineIndex].widgets = newWidgetsOnDropLine;

        const newDashboard: Dashboard = {
            ...dashboard,
            lines: newLines
        }
        put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    console.error("Error adding widget to dashboard", response);
                }
            } else {
                const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                updateDashboards(newDashboards);
                updateSelectedDashboard(newDashboard);
            }
            setIsHover(false);
        }).finally(() => {
            setIsDraggingOverRight(false);
            setIsDraggingOverLeft(false);
            setDraggingWidget({ draggingWidget: null });
        })

    }

    const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        setIsDragging(true);
        setDraggingWidget({ draggingWidget: { lineIndex, widgetIndex } });
    }

    const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        setIsDragging(false);
    }

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const width = rect.right - rect.left;
        const x = e.clientX - rect.left;
        const height = rect.bottom - rect.top;
        const y = e.clientY - rect.top;
        const isDraggingOverLine = y > height * 3 / 4 || y < height / 4;
        if (x > width * 1 / 2 && !isDraggingOverLine) {
            setIsDraggingOverRight(true);
            setIsDraggingOverLeft(false);
        } else if (x < width * 1 / 2 && !isDraggingOverLine) {
            setIsDraggingOverRight(false);
            setIsDraggingOverLeft(true);
        } else {
            setIsDraggingOverRight(false);
            setIsDraggingOverLeft(false);
        }
    }

    const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDraggingOverRight(false);
            setIsDraggingOverLeft(false);
        }
    }

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dropWidget();
    }

    const style = {
        opacity: isDragging ? 0.3 : 1,
        transition: "all 0.2s ease-in-out",
        transform: isDraggingOverLeft ? "translateX(20px)" : isDraggingOverRight ? "translateX(-20px)" : "translateX(0px)",
        borderLeft: isDraggingOverLeft ? "10px solid " + wsColor : "none",
        borderRight: isDraggingOverRight ? "10px solid " + wsColor : "none"
    };

    const zIndex = (isHover && !isDragging) ? "z-10" : "z-0";

    return (
        <div id={`widget-${lineIndex}-${widgetIndex}`} className={`flex flex-col justify-center items-center relative ${editMode ? "cursor-grab" : "cursor-default"} transition-all duration-200 ${isDragging ? "opacity-50" : "opacity-100"} ${zIndex}`} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)} draggable={editMode} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} style={style}>
            <div className={`flex flex-row justify-center items-center w-max h-full gap-1 relative ${zIndex}`}>
                {widget.type === KPIWidgetType && <KPICard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-kpi"} widget={widget} />}
                {widget.type === PieChartWidgetType && <PieChartCard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-pie"} widget={widget} />}
                {widget.type === BarChartWidgetType && <BarChartCard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-bar"} widget={widget} />}
                {widget.type === RadarChartWidgetType && <RadarChartCard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-radar"} widget={widget} />}
                {widget.type === LineChartWidgetType && <LineChartCard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-line"} widget={widget} />}
                {widget.type === TextWidgetType && <TextCard key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-text"} widget={widget} />}
                {editMode && !isDragging && <WidgetPlusDialog dashboard={dashboard} lineIndex={lineIndex} widgetIndex={widgetIndex} pos="left" display={isHover} addWidget={addWidget} lineNumber={lineNumber} widgetNumber={widgetNumber} />}
                {editMode && !isDragging && <WidgetMenuDialog dashboard={dashboard} lineIndex={lineIndex} widgetIndex={widgetIndex} widget={widget} display={isHover} onEditConfirmed={onEditConfirmed} onDuplicateConfirmed={onDuplicateConfirmed} onDeleteConfirmed={onDeleteConfirmed} />}
                {editMode && !isDragging && <WidgetPlusDialog dashboard={dashboard} lineIndex={lineIndex} widgetIndex={widgetIndex} pos="right" display={isHover} addWidget={addWidget} lineNumber={lineNumber} widgetNumber={widgetNumber} />}
            </div>
            {editMode && !isDragging && <WidgetPlusDialog dashboard={dashboard} lineIndex={lineIndex} widgetIndex={0} pos="top" display={isHover} addWidget={addWidget} lineNumber={lineNumber} widgetNumber={widgetNumber} />}
            {editMode && !isDragging && <WidgetPlusDialog dashboard={dashboard} lineIndex={lineIndex} widgetIndex={widgetIndex} pos="bottom" display={isHover} addWidget={addWidget} lineNumber={lineNumber} widgetNumber={widgetNumber} />}
        </div>
    );
}

function NewFirstWidgetButton({ dashboard }: { dashboard: Dashboard }) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { t } = useTranslation();
    const workspace = useModelStore(state => state.selectedWorkspace);
    const dashboards = useModelStore(state => state.dashboards);
    const updateDashboards = useModelStore(state => state.updateDashboards);
    const updateSelectedDashboard = useModelStore(state => state.updateSelectedDashboard);

    function addWidget(widget: Widget) {
        if (widget) {
            let newLines = dashboard.lines ?? [];
            newLines.push({
                widgets: [widget],
            });
            const newDashboard: Dashboard = {
                ...dashboard,
                lines: newLines
            }
            put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace!._key}/dashboards/${dashboard._key}`, newDashboard).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        console.error("Error adding widget to dashboard", response);
                    }
                } else {
                    const newDashboards = dashboards?.map(d => d._key === dashboard._key ? newDashboard : d) ?? [newDashboard];
                    updateDashboards(newDashboards);
                    updateSelectedDashboard(newDashboard);
                    setIsDialogOpen(false);
                }
            })
        }
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger className="flex flex-row justify-center items-center gap-2">
                <Plus className="w-10 h-10" />
                <p className="text-compozz-purple-dark text-2xl font-bold">{t("dashboard.button.new.widget.text")}</p>
            </DialogTrigger>
            <DialogContent className="flex flex-col justify-start items-start w-full h-5/6 max-w-7xl">
                <DialogHeader className="h-max">
                    <DialogTitle >{t("dashboard.dialog.new.widget.title")}</DialogTitle>
                </DialogHeader>
                <DialogDescription hidden>{t("dashboard.dialog.new.widget.description")}</DialogDescription>
                <WidgetConfig initialWidget={undefined} onValidation={addWidget} onCancel={() => setIsDialogOpen(false)} />
            </DialogContent>
        </Dialog>
    )
}


interface WidgetPlusDialogProps {
    dashboard: Dashboard;
    lineIndex: number;
    widgetIndex: number;
    pos: "top" | "right" | "bottom" | "left";
    display: boolean;
    addWidget: (widget: Widget, pos: "top" | "right" | "bottom" | "left") => void;
    lineNumber: number;
    widgetNumber: number;
}
function WidgetPlusDialog({ dashboard, lineIndex, widgetIndex, pos, display, addWidget, lineNumber, widgetNumber }: WidgetPlusDialogProps) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { t } = useTranslation();

    let position = "";
    switch (pos) {
        case "top":
            position = "top-[0px] -translate-y-[calc(theme('spacing.4')+theme('spacing.px'))]";
            break;
        case "right":
            position = "right-[0px] translate-x-[calc(theme('spacing.4')+theme('spacing.px'))]";
            break;
        case "bottom":
            position = "bottom-[0px] translate-y-[calc(theme('spacing.4')+theme('spacing.px'))]";
            break;
        case "left":
            position = "left-[0px] -translate-x-[calc(theme('spacing.4')+theme('spacing.px'))]";
            break;
    }

    const onPlusClick = () => {
        setIsDialogOpen(true);
    }

    const onValidation = useCallback((widget: Widget) => {
        addWidget(widget, pos);
        setIsDialogOpen(false);
    }, [pos]);

    const zIndex = (display) ? "z-10" : "z-0";

    return (
        <Dialog key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-plus-" + pos} open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger onClick={onPlusClick} className={`flex flex-col justify-center items-center ${display ? "opacity-100" : "opacity-0"} transition-all duration-200 cursor-pointer absolute ${position} rounded-full bg-white border-1 border-gray-200 p-1 hover:scale-125 ${zIndex}`}>
                <Plus className="w-4 h-4" />
            </DialogTrigger>
            <DialogContent className="flex flex-col justify-start items-start w-full h-5/6 max-w-7xl">
                <DialogHeader className="h-max">
                    <DialogTitle >{t("dashboard.dialog.new.widget.title")}</DialogTitle>
                </DialogHeader>
                <DialogDescription hidden>{t("dashboard.dialog.new.widget.description")}</DialogDescription>
                <WidgetConfig initialWidget={undefined} onValidation={onValidation} onCancel={() => setIsDialogOpen(false)} />
            </DialogContent>
        </Dialog>
    )
}

function WidgetMenuDialog({ dashboard, lineIndex, widgetIndex, widget, display, onEditConfirmed, onDuplicateConfirmed, onDeleteConfirmed }: { dashboard: Dashboard, lineIndex: number, widgetIndex: number, widget: Widget, display: boolean, onEditConfirmed: (lineIndex: number, widgetIndex: number, newWidget: Widget) => void, onDuplicateConfirmed: (lineIndex: number, widgetIndex: number) => void, onDeleteConfirmed: (lineIndex: number, widgetIndex: number) => void }) {
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const cancelDeletionHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const confirmDeletionHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const onCancelDeletion = () => {
        setIsDeleteDialogOpen(false);
        setIsMenuOpen(false);
    }

    const onDelete = () => {
        setIsDeleteDialogOpen(false);
        setIsMenuOpen(false);
        onDeleteConfirmed(lineIndex, widgetIndex);
    }

    const onDuplicate = () => {
        setIsMenuOpen(false);
        onDuplicateConfirmed(lineIndex, widgetIndex);
    }

    const onEditValidation = (newWidget: Widget) => {
        setIsMenuOpen(false);
        onEditConfirmed(lineIndex, widgetIndex, newWidget);
    }

    let position = "top-2 right-2";
    return (
        <Popover key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-menu"} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <PopoverTrigger className={`flex flex-col justify-center items-center ${display ? "opacity-100" : "opacity-0"} transition-all duration-200 cursor-pointer absolute ${position} rounded-full bg-white border-0 border-gray-200 p-1 hover:bg-gray-100 z-10`}>
                <EllipsisVertical className="w-4 h-4" />
            </PopoverTrigger>
            <PopoverContent className="w-max p-1">
                <ul>
                    <li>
                        <Dialog key={dashboard._key + "-" + lineIndex + "-" + widgetIndex + "-edit"} open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                            <DialogTrigger className={TW_DashboardWidgetMenuItem}>
                                <Edit2 className="w-4 h-4" />{t("dashboard.widget.menu.edit.title")}
                            </DialogTrigger>
                            <DialogContent className="flex flex-col justify-start items-start w-full h-5/6 max-w-7xl">
                                <DialogHeader className="h-max">
                                    <DialogTitle >{t("dashboard.dialog.new.widget.title")}</DialogTitle>
                                </DialogHeader>
                                <DialogDescription hidden>{t("dashboard.dialog.new.widget.description")}</DialogDescription>
                                <WidgetConfig initialWidget={widget} onValidation={onEditValidation} onCancel={() => setIsEditDialogOpen(false)} />
                            </DialogContent>
                        </Dialog>
                    </li>
                    <li>
                        <button className={TW_DashboardWidgetMenuItem} onClick={onDuplicate}>
                            <Copy className="w-4 h-4" />{t("dashboard.widget.menu.duplicate.title")}
                        </button>
                    </li>
                    <li>
                        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                            <DialogTrigger className={TW_DashboardWidgetMenuItem}>
                                <Trash2 className="w-4 h-4" />{t("dashboard.widget.menu.delete.title")}
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>{t("dashboard.widget.menu.delete.title")}</DialogTitle>
                                </DialogHeader>
                                <DialogDescription>{translateHtml(t, "dashboard.widget.menu.delete.description", [])}</DialogDescription>
                                <div className={TW_ButtonList}>
                                    <Button className={TW_AppDialogButton} onClick={onCancelDeletion} {...cancelDeletionHover}>{t("general.button.cancel.text")}</Button>
                                    <Button className={TW_AppDialogButton} onClick={onDelete} {...confirmDeletionHover}>{t("general.button.confirm.text")}</Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </li>
                </ul>
            </PopoverContent>
        </Popover>
    )
}